import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Trash2, Plus, Crown, Shield, Edit, Save, X, UserPlus } from "lucide-react";
import { useAuth } from "@/context/auth-provider";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { formatPhoneNumber } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface PlatformUser {
  id: string;
  user_id: string;
  role: 'super_admin' | 'support' | 'member' | 'guest';
  created_at: string;
  updated_at: string;
  profile?: {
    id: string;
    name: string | null;
    email: string | null;
    phone_number: string | null;
  };
}

interface AdminsListProps {
  isSuperAdmin: boolean;
}

const AdminsList = ({ isSuperAdmin }: AdminsListProps) => {
  const { user } = useAuth();
  const [admins, setAdmins] = useState<PlatformUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState<'super_admin' | 'support'>('support');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<PlatformUser>>({});
  const [showAddDialog, setShowAddDialog] = useState(false);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      
      // First get platform roles
      const { data: rolesData, error: rolesError } = await supabase
        .from('platform_user_roles')
        .select('id, user_id, role, created_at, updated_at')
        .in('role', ['super_admin', 'support'])
        .order('created_at', { ascending: false });

      if (rolesError) throw rolesError;

      if (!rolesData || rolesData.length === 0) {
        setAdmins([]);
        return;
      }

      // Then get corresponding profiles
      const userIds = rolesData.map(role => role.user_id);
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, name, email, phone_number')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      // Combine the data
      const adminData = rolesData.map(role => {
        const profile = profilesData?.find(p => p.id === role.user_id);
        return {
          ...role,
          profile: profile || null
        };
      });

      setAdmins(adminData);
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast({
        title: "Error",
        description: "Failed to fetch admin users",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const addAdmin = async () => {
    if (!newPhoneNumber || !newEmail || !newName) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      // First, create or get the profile
      const { data: existingProfile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('phone_number', newPhoneNumber)
        .single();

      let profileId: string;
      
      if (existingProfile) {
        profileId = existingProfile.id;
        // Update existing profile
        await supabase
          .from('profiles')
          .update({
            name: newName,
            email: newEmail,
          })
          .eq('id', profileId);
      } else {
        // Create new profile
        const { data: newProfile, error: createError } = await supabase
          .from('profiles')
          .insert({
            name: newName,
            email: newEmail,
            phone_number: newPhoneNumber,
          })
          .select('id')
          .single();

        if (createError) throw createError;
        profileId = newProfile.id;
      }

      // Create platform role
      const { error: roleError } = await supabase
        .from('platform_user_roles')
        .upsert({
          user_id: profileId,
          role: newRole,
        });

      if (roleError) throw roleError;

      toast({
        title: "Success",
        description: "Admin user added successfully",
      });

      setNewPhoneNumber('');
      setNewEmail('');
      setNewName('');
      setNewRole('support');
      setShowAddDialog(false);
      fetchAdmins();
    } catch (error) {
      console.error('Error adding admin:', error);
      toast({
        title: "Error",
        description: "Failed to add admin user",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteAdmin = async (adminId: string) => {
    try {
      const { error } = await supabase
        .from('platform_user_roles')
        .delete()
        .eq('id', adminId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Admin user removed successfully",
      });
      fetchAdmins();
    } catch (error) {
      console.error('Error deleting admin:', error);
      toast({
        title: "Error",
        description: "Failed to remove admin user",
        variant: "destructive",
      });
    }
  };

  const startEdit = (admin: PlatformUser) => {
    setEditingAdmin(admin.id);
    setEditData({
      role: admin.role,
      profile: admin.profile
    });
  };

  const cancelEdit = () => {
    setEditingAdmin(null);
    setEditData({});
  };

  const updateAdminField = (field: string, value: any) => {
    if (field === 'role') {
      setEditData({
        ...editData,
        [field]: value
      });
    } else {
      setEditData({
        ...editData,
        profile: {
          ...editData.profile,
          [field]: value
        }
      });
    }
  };

  const saveChanges = async (admin: PlatformUser) => {
    try {
      // Update role if changed
      if (editData.role && editData.role !== admin.role) {
        const { error: roleError } = await supabase
          .from('platform_user_roles')
          .update({ role: editData.role })
          .eq('id', admin.id);

        if (roleError) throw roleError;
      }

      // Update profile if changed
      if (editData.profile && admin.profile) {
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            name: editData.profile.name || admin.profile.name,
            email: editData.profile.email || admin.profile.email,
          })
          .eq('id', admin.profile.id);

        if (profileError) throw profileError;
      }

      toast({
        title: "Success",
        description: "Admin information updated",
      });
      
      setEditingAdmin(null);
      fetchAdmins();
    } catch (error) {
      console.error('Error updating admin:', error);
      toast({
        title: "Error",
        description: "Failed to update admin information",
        variant: "destructive",
      });
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'super_admin':
        return <Crown className="h-4 w-4" />;
      case 'support':
        return <Shield className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'default';
      case 'support':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Platform Administrators</h3>
        {isSuperAdmin && (
          <Button onClick={() => setShowAddDialog(true)} size="sm">
            <UserPlus className="h-4 w-4 mr-2" />
            Add Admin
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Added</TableHead>
                {isSuperAdmin && <TableHead className="w-[100px]">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {admins.map((admin) => (
                <TableRow key={admin.id}>
                  <TableCell>
                    {editingAdmin === admin.id ? (
                      <Input
                        value={editData.profile?.name || admin.profile?.name || ''}
                        onChange={(e) => updateAdminField('name', e.target.value)}
                        placeholder="Name"
                      />
                    ) : (
                      <div className="font-medium">
                        {admin.profile?.name || 'No name'}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {editingAdmin === admin.id ? (
                        <Input
                          type="email"
                          value={editData.profile?.email || admin.profile?.email || ''}
                          onChange={(e) => updateAdminField('email', e.target.value)}
                          placeholder="Email"
                        />
                      ) : (
                        <>
                          <div className="text-sm text-muted-foreground">
                            {admin.profile?.email || 'No email'}
                          </div>
                          <div className="text-sm font-mono">
                            {formatPhoneNumber(admin.profile?.phone_number || '')}
                          </div>
                        </>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {editingAdmin === admin.id ? (
                      <Select
                        value={editData.role || admin.role}
                        onValueChange={(value) => updateAdminField('role', value)}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="support">Support</SelectItem>
                          <SelectItem value="super_admin">Super Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant={getRoleColor(admin.role)} className="gap-1">
                        {getRoleIcon(admin.role)}
                        {admin.role === 'super_admin' ? 'Super Admin' : 'Support'}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-muted-foreground">
                      {new Date(admin.created_at).toLocaleDateString()}
                    </div>
                  </TableCell>
                  {isSuperAdmin && (
                    <TableCell>
                      {editingAdmin === admin.id ? (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => saveChanges(admin)}
                          >
                            <Save className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={cancelEdit}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => startEdit(admin)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deleteAdmin(admin.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {admins.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isSuperAdmin ? 5 : 4} className="text-center text-muted-foreground py-8">
                    No platform administrators found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Admin Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Platform Administrator</DialogTitle>
            <DialogDescription>
              Add a new administrator to the platform. They will have access to admin features.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Name</label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Full name"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Phone Number</label>
              <PhoneInput
                value={newPhoneNumber}
                onChange={(value) => setNewPhoneNumber(value || '')}
                defaultCountry="ZA"
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Email</label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="email@example.com"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Role</label>
              <Select value={newRole} onValueChange={(value: 'super_admin' | 'support') => setNewRole(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="support">Support</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={addAdmin} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminsList;